import React from 'react';
import { st, classes } from './Body.st.css';
import { MainComponents } from '../../types';
import { RenderLocationProvider } from '../useRenderLocation';
import {
  SectionNotification,
  SectionNotificationType as NOTIFICATION_TYPE,
} from 'wix-ui-tpa/cssVars';
import {
  AVAILABILITY_MESSAGE_TYPE,
  BodyViewModel,
} from '../../../../service-page-view-model/body-view-model/bodyViewModel';
import { useSettings } from '@wix/tpa-settings/react';
import settingsParams from '../../settingsParams';
import { useTranslation, useEnvironment, useExperiments } from '@wix/yoshi-flow-editor';

type BodyProps = {
  viewModel: BodyViewModel;
  className?: string;
};

const BodyComponent: React.FC<BodyProps> = ({
  viewModel,
  className,
  children,
}) => {
  const { t } = useTranslation();
  const settings = useSettings();
  const { experiments } = useExperiments();
  const { isRTL } = useEnvironment();
  const getMessageText = (messageType: AVAILABILITY_MESSAGE_TYPE) => {
    const showDefaultValue = experiments.enabled(
      'specs.bookings.showDefaultValue',
    );
    switch (messageType) {
      case AVAILABILITY_MESSAGE_TYPE.FULLY_BOOKED:
        return showDefaultValue
          ? settings.get(settingsParams.fullCourseMessageText) ||
              settings.getDefaultValue(settingsParams.fullCourseMessageText)
          : settings.get(settingsParams.fullCourseMessageText);
      case AVAILABILITY_MESSAGE_TYPE.TOO_EARLY:
        return t('app.widget.uou-messages.book-is-not-open-yet', {
          timeUntilServiceIsOpen: viewModel.timeUntilServiceIsOpen, // TODO - understand how to test it properly
        });
      case AVAILABILITY_MESSAGE_TYPE.STARTED_AND_BOOKABLE:
        return showDefaultValue
          ? settings.get(settingsParams.bookStartedCourseMessageText) ||
              settings.getDefaultValue(
                settingsParams.bookStartedCourseMessageText,
              )
          : settings.get(settingsParams.bookStartedCourseMessageText);
      case AVAILABILITY_MESSAGE_TYPE.TOO_LATE:
        return showDefaultValue
          ? settings.get(settingsParams.unBookableCourseMessageText) ||
              settings.getDefaultValue(
                settingsParams.unBookableCourseMessageText,
              )
          : settings.get(settingsParams.unBookableCourseMessageText);
      case AVAILABILITY_MESSAGE_TYPE.NOT_AVAILABLE:
      default:
        return showDefaultValue
          ? settings.get(settingsParams.serviceUnavailableMessageText) ||
              settings.getDefaultValue(
                settingsParams.serviceUnavailableMessageText,
              )
          : settings.get(settingsParams.serviceUnavailableMessageText);
    }
  };
  return (
    <RenderLocationProvider value={MainComponents.BODY}>
      <section
        aria-label={t('body-aria-label.service-name-details', {
          serviceName: viewModel.serviceName,
        })}
        className={st(
          classes.root,
          {
            alignment: settings.get(settingsParams.bodyAlignment),
            columnAlignment: settings.get(settingsParams.columnPosition),
            isRTL,
          },
          className,
        )}
        data-hook="body"
      >
        <div className={classes.columnWrapper}>
          {viewModel.messageType && (
            <div
              role="status"
              data-hook="no-book-button-message"
              className={classes.notificationBanner}
            >
              <SectionNotification
                type={NOTIFICATION_TYPE.wired}
                className={st(classes.sectionNotification)}
              >
                <SectionNotification.Text>
                  {getMessageText(viewModel.messageType)}
                </SectionNotification.Text>
              </SectionNotification>
            </div>
          )}
          <div data-hook="body-children-wrapper">{children}</div>
        </div>
      </section>
    </RenderLocationProvider>
  );
};

export default BodyComponent;
